import React from 'react';

import { cn } from '@/lib/utils';

interface TypographyProps {
  id?: string;
  children: React.ReactNode;
  className?: string;
}

interface TypographyAsProps extends TypographyProps {
  as?: React.ElementType;
}

export function TypographyPageHeading({
  children,
  className,
}: TypographyProps) {
  return (
    <TypographyH1 className={cn('text-3xl lg:text-4xl', className)}>
      {children}
    </TypographyH1>
  );
}

export function TypographyH1New({
  as,
  children,
  className,
}: TypographyAsProps) {
  const Component = as || 'h1';
  return (
    <Component
      className={cn(
        'scroll-m-20 text-4xl font-black tracking-tight lg:text-5xl',
        'font-geist-sans',
        className,
      )}
    >
      {children}
    </Component>
  );
}

export function TypographyH1({ as, children, className }: TypographyAsProps) {
  const Component = as || 'h1';
  return (
    <Component
      className={cn(
        'scroll-m-20 text-4xl font-black tracking-tight lg:text-5xl',
        className,
      )}
    >
      {children}
    </Component>
  );
}

export function TypographyH2({ id, children, className }: TypographyProps) {
  return (
    <h2
      id={id}
      className={cn(
        'scroll-m-20 pb-2 text-3xl font-semibold tracking-tighter first:mt-0',
        className,
      )}
    >
      {children}
    </h2>
  );
}

export function TypographyH3({ children, className }: TypographyProps) {
  return (
    <h3
      className={cn(
        'scroll-m-20 text-2xl font-semibold tracking-tight',
        className,
      )}
    >
      {children}
    </h3>
  );
}

export function TypographyH4({ children, className }: TypographyProps) {
  return (
    <h4
      className={cn(
        'scroll-m-20 text-lg lg:text-xl font-semibold tracking-tight',
        className,
      )}
    >
      {children}
    </h4>
  );
}

export function TypographyH5({ children, className }: TypographyProps) {
  return (
    <h5
      className={cn(
        'scroll-m-20 text-lg font-semibold tracking-tight',
        className,
      )}
    >
      {children}
    </h5>
  );
}

export function TypographyP({ children, className, as }: TypographyAsProps) {
  const Component = as || 'p';
  return (
    <Component className={cn('leading-7 not-first:mt-6', className)}>
      {children}
    </Component>
  );
}

export function TypographyList({ children, className }: TypographyProps) {
  return (
    <ul className={cn('my-6 ml-6 list-disc [&>li]:mt-2', className)}>
      {children}
    </ul>
  );
}

export function TypographyInlineCode({ children, className }: TypographyProps) {
  return (
    <code
      className={cn(
        'relative rounded-sm bg-halfmast-1000 px-[0.3rem] py-[0.1rem] font-mono text-sm font-semibold text-greenhorn-500',
        className,
      )}
    >
      {children}
    </code>
  );
}

export function TypographyLead({ children, className }: TypographyProps) {
  return (
    <p className={cn('text-xl text-muted-foreground', className)}>{children}</p>
  );
}

export function TypographyLarge({ children, className }: TypographyProps) {
  return (
    <div className={cn('text-lg font-semibold', className)}>{children}</div>
  );
}

export function TypographySmall({
  children,
  className,
  as,
}: TypographyAsProps) {
  const Component = as || 'small';
  return (
    <Component className={cn('text-sm font-medium', className)}>
      {children}
    </Component>
  );
}

export function TypographyMuted({
  children,
  className,
  as,
}: TypographyAsProps) {
  const Component = as || 'p';
  return (
    <Component className={cn('text-sm text-muted-foreground', className)}>
      {children}
    </Component>
  );
}
